import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const NevixaLandingPage = ({ branding = {} }) => {
  const navigate = useNavigate();
  const { primaryColor = '#6366f1', accentColor = '#06b6d4', companyName = 'Nevixa' } = branding;

  const [scrolled, setScrolled] = useState(false);
  const [fadeVisible, setFadeVisible] = useState({});

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setFadeVisible(prev => ({ ...prev, [entry.target.id]: true }));
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.fade-in').forEach(el => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleLogin = (type) => {
    switch (type) {
      case 'paas': navigate('/paas-admin/login'); break;
      case 'saas': navigate('/saas-admin/login'); break;
      case 'client': navigate('/client-admin/login'); break;
      case 'app': navigate('/login'); break;
      default: navigate('/login');
    }
  };

  const gradientStyle = (dir = '135deg') => ({
    background: `linear-gradient(${dir}, ${primaryColor} 0%, ${accentColor} 100%)`
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0f', color: '#ffffff', fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @keyframes fadeInUp { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
        @keyframes pulse { 0%,100%{opacity:0.5;transform:translateX(-50%) scale(1)}50%{opacity:0.8;transform:translateX(-50%) scale(1.1)} }
        @keyframes blink { 0%,100%{opacity:1}50%{opacity:0.4} }
        .fade-in{opacity:0;transform:translateY(30px);transition:all 0.6s ease}
        .fade-in.visible{opacity:1;transform:translateY(0)}
        .hero-glow{position:absolute;top:20%;left:50%;transform:translateX(-50%);width:800px;height:400px;background:radial-gradient(ellipse,${primaryColor}33,transparent 60%);filter:blur(80px);animation:pulse 8s ease-in-out infinite}
        .hero-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(99,102,241,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,0.03) 1px,transparent 1px);background-size:60px 60px;mask-image:radial-gradient(ellipse 70% 70% at 50% 50%,black 20%,transparent 70%)}
        .hero-badge{animation:fadeInUp 0.6s ease forwards}
        .hero h1{animation:fadeInUp 0.6s ease 0.1s forwards;opacity:0}
        .hero-subtitle{animation:fadeInUp 0.6s ease 0.2s forwards;opacity:0}
        .hero-buttons{animation:fadeInUp 0.6s ease 0.3s forwards;opacity:0}
        .hero-stats{animation:fadeInUp 0.6s ease 0.4s forwards;opacity:0}
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? 'bg-opacity-95 py-3' : 'bg-opacity-80 py-4'}`}
        style={{ background: 'rgba(10,10,15,0.8)', backdropFilter: 'blur(20px)', borderBottom: '1px solid #27272a' }}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href="#" className="flex items-center gap-2 text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <div className="w-9 h-9 rounded flex items-center justify-center text-lg font-extrabold" style={gradientStyle()}>{companyName[0]}</div>
            {companyName}
          </a>
          <ul className="hidden md:flex items-center gap-8 list-none" style={{ color: '#a1a1aa' }}>
            {['platform', 'debt-recovery', 'iot', 'ai', 'use-cases'].map(item => (
              <li key={item}><a href={`#${item}`} onClick={(e) => { e.preventDefault(); scrollTo(item); }}
                className="text-sm font-medium hover:text-white transition-colors capitalize">{item.replace('-', ' ')}</a></li>
            ))}
          </ul>
          <div className="flex items-center gap-3">
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('contact'); }}
              className="px-5 py-2.5 rounded-md text-sm font-semibold text-white transition-all hover:-translate-y-0.5"
              style={gradientStyle()}>Request Demo</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0" style={{
          background: `radial-gradient(ellipse 80% 50% at 50% -20%, ${primaryColor}40, transparent), radial-gradient(ellipse 60% 40% at 80% 60%, #8b5cf640, transparent), radial-gradient(ellipse 50% 30% at 20% 80%, ${accentColor}1a, transparent)`
        }}></div>
        <div className="hero-grid absolute inset-0"></div>
        <div className="hero-glow"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center" style={{ maxWidth: '900px' }}>
          <div className="hero-badge inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6" style={{ background: '#1a1a25', borderColor: '#27272a' }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#10b981', animation: 'blink 2s ease-in-out infinite' }}></span>
            <span className="text-sm font-medium" style={{ color: '#a1a1aa' }}>Now Available: Enterprise AI Suite</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Build the Future with <span style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>AI, IoT</span>, and Intelligent Automation
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            {companyName} is a unified enterprise SaaS platform empowering financial institutions, industries, and enterprises with cutting-edge AI, IoT, and automation solutions.
          </p>
          <div className="hero-buttons flex items-center justify-center gap-4 flex-wrap">
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('contact'); }}
              className="px-7 py-3.5 rounded-lg text-base font-semibold text-white inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all"
              style={gradientStyle()}>
              Request Demo
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </a>
            <a href="#platform" onClick={(e) => { e.preventDefault(); scrollTo('platform'); }}
              className="px-7 py-3.5 rounded-lg text-base font-semibold border text-white hover:bg-white/5 transition-all"
              style={{ borderColor: '#3f3f46' }}>Explore Platform</a>
          </div>
          <div className="hero-stats flex items-center justify-center gap-12 mt-20 pt-12" style={{ borderTop: '1px solid #27272a' }}>
            {[{ v: '500+', l: 'Enterprise Clients' }, { v: '99.9%', l: 'Uptime SLA' }, { v: '50M+', l: 'API Calls/Day' }].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontFamily: "'Space Grotesk', sans-serif" }}>{s.v}</div>
                <div className="text-sm mt-1" style={{ color: '#71717a' }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section id="platform" className="py-24" style={{ background: '#12121a' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16" style={{ maxWidth: '700px', margin: '0 auto 64px' }}>
            <span className="inline-block text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: primaryColor }}>Our Platform</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>One Platform, Three Powerful Solutions</h2>
            <p className="text-lg" style={{ color: '#a1a1aa', lineHeight: 1.7 }}>Comprehensive enterprise solutions tailored for modern businesses.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '💰', title: 'Debt Recovery SaaS', desc: 'AI-driven recovery management platform for banks, NBFCs, and financial institutions to automate collections.', features: ['Automated collections workflows', 'AI recovery prioritization', 'Legal case tracking', 'Compliance & audit trails'] },
              { icon: '📡', title: 'IoT SaaS Platform', desc: 'Cloud platform to manage connected devices, real-time monitoring, and predictive maintenance.', features: ['Device management', 'Real-time monitoring dashboards', 'Predictive maintenance', 'Data streaming & alerts'] },
              { icon: '🤖', title: 'AI SaaS Platform', desc: 'AI tools and APIs for automation, analytics, document intelligence, and workflow optimization.', features: ['AI automation tools', 'Document processing', 'NLP & machine learning', 'Decision intelligence'] }
            ].map((f, i) => (
              <div key={i} id={`feature-${i}`} className="fade-in rounded-2xl p-8 border transition-all duration-300 hover:-translate-y-1 group"
                style={{ background: '#15151f', borderColor: '#27272a' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = primaryColor; e.currentTarget.style.background = '#1c1c28'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#27272a'; e.currentTarget.style.background = '#15151f'; }}>
                <div className="w-14 h-14 rounded-xl mb-5 flex items-center justify-center text-3xl" style={{ background: '#1a1a25' }}>{f.icon}</div>
                <h3 className="text-2xl font-semibold mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{f.title}</h3>
                <p className="mb-5" style={{ color: '#a1a1aa', fontSize: '15px', lineHeight: 1.7 }}>{f.desc}</p>
                <ul className="space-y-2">
                  {f.features.map((feat, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-sm" style={{ color: '#a1a1aa' }}>
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: `${primaryColor}33`, color: primaryColor }}>✓</span>
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Debt Recovery */}
      <section id="debt-recovery" className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div className="rounded-2xl border overflow-hidden" style={{ background: '#15151f', borderColor: '#27272a', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
            <div className="flex items-center gap-2 px-4 py-3" style={{ background: '#1a1a25', borderBottom: '1px solid #27272a' }}>
              <span className="w-3 h-3 rounded-full" style={{ background: '#ef4444' }}></span>
              <span className="w-3 h-3 rounded-full" style={{ background: '#f59e0b' }}></span>
              <span className="w-3 h-3 rounded-full" style={{ background: '#10b981' }}></span>
            </div>
            <div className="p-6 min-h-64 flex flex-col items-center justify-center text-center" style={{ color: '#71717a' }}>
              <div className="w-16 h-16 rounded-xl mb-4 flex items-center justify-center text-3xl" style={{ background: '#1a1a25' }}>💰</div>
              <p>Debt Recovery Dashboard UI</p>
              <p className="text-xs mt-2">Dashboard Screenshot Placeholder</p>
            </div>
          </div>
          <div>
            <h2 className="text-4xl font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Debt Recovery <span style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>SaaS</span>
            </h2>
            <p className="text-lg mb-8" style={{ color: '#a1a1aa', lineHeight: 1.8 }}>Transform your collections process with AI-powered automation.</p>
            <div className="grid grid-cols-2 gap-5 mb-8">
              {[{ icon: '⚡', t: 'Automated Workflows', p: 'Streamlined collection pipelines' }, { icon: '🧠', t: 'AI Prioritization', p: 'Smart case routing' }, { icon: '⚖️', t: 'Legal Tracking', p: 'End-to-end case management' }, { icon: '📊', t: 'Analytics', p: 'Recovery performance insights' }].map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl border" style={{ background: '#15151f', borderColor: '#27272a' }}>
                  <div className="w-8 h-8 rounded flex items-center justify-center text-lg flex-shrink-0" style={{ background: `${primaryColor}26`, color: primaryColor }}>{f.icon}</div>
                  <div>
                    <h4 className="text-sm font-semibold mb-0.5">{f.t}</h4>
                    <p className="text-xs" style={{ color: '#71717a' }}>{f.p}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 rounded-xl border" style={{ background: '#1a1a25', borderColor: '#27272a' }}>
              <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#71717a' }}>Target Users</div>
              <div className="flex flex-wrap gap-2">
                {['Banks', 'NBFCs', 'Asset Reconstruction', 'Collection Agencies'].map(t => (
                  <span key={t} className="px-3 py-1.5 rounded text-sm border" style={{ background: '#15151f', borderColor: '#27272a', color: '#a1a1aa' }}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IoT */}
      <section id="iot" className="py-24" style={{ background: '#12121a' }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              IoT <span style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>SaaS</span> Platform
            </h2>
            <p className="text-lg mb-8" style={{ color: '#a1a1aa', lineHeight: 1.8 }}>Connect, monitor, and manage your entire device ecosystem from a single platform.</p>
            <div className="grid grid-cols-2 gap-5 mb-8">
              {[{ icon: '📱', t: 'Device Management', p: 'Scale IoT deployments' }, { icon: '📈', t: 'Real-time Dashboards', p: 'Live monitoring & alerts' }, { icon: '🔧', t: 'Predictive Maintenance', p: 'AI-powered predictions' }, { icon: '🔌', t: 'Integration APIs', p: 'Connect any system' }].map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl border" style={{ background: '#15151f', borderColor: '#27272a' }}>
                  <div className="w-8 h-8 rounded flex items-center justify-center text-lg flex-shrink-0" style={{ background: `${primaryColor}26`, color: primaryColor }}>{f.icon}</div>
                  <div>
                    <h4 className="text-sm font-semibold mb-0.5">{f.t}</h4>
                    <p className="text-xs" style={{ color: '#71717a' }}>{f.p}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 rounded-xl border" style={{ background: '#1a1a25', borderColor: '#27272a' }}>
              <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#71717a' }}>Industries</div>
              <div className="flex flex-wrap gap-2">
                {['Manufacturing', 'Smart Buildings', 'Logistics', 'Energy & Utilities'].map(t => (
                  <span key={t} className="px-3 py-1.5 rounded text-sm border" style={{ background: '#15151f', borderColor: '#27272a', color: '#a1a1aa' }}>{t}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border overflow-hidden" style={{ background: '#15151f', borderColor: '#27272a', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
            <div className="flex items-center gap-2 px-4 py-3" style={{ background: '#1a1a25', borderBottom: '1px solid #27272a' }}>
              <span className="w-3 h-3 rounded-full" style={{ background: '#ef4444' }}></span>
              <span className="w-3 h-3 rounded-full" style={{ background: '#f59e0b' }}></span>
              <span className="w-3 h-3 rounded-full" style={{ background: '#10b981' }}></span>
            </div>
            <div className="p-6 min-h-64 flex flex-col items-center justify-center text-center" style={{ color: '#71717a' }}>
              <div className="w-16 h-16 rounded-xl mb-4 flex items-center justify-center text-3xl" style={{ background: '#1a1a25' }}>📡</div>
              <p>IoT Dashboard UI</p>
              <p className="text-xs mt-2">Dashboard Screenshot Placeholder</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI */}
      <section id="ai" className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div className="rounded-2xl border overflow-hidden" style={{ background: '#15151f', borderColor: '#27272a', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
            <div className="flex items-center gap-2 px-4 py-3" style={{ background: '#1a1a25', borderBottom: '1px solid #27272a' }}>
              <span className="w-3 h-3 rounded-full" style={{ background: '#ef4444' }}></span>
              <span className="w-3 h-3 rounded-full" style={{ background: '#f59e0b' }}></span>
              <span className="w-3 h-3 rounded-full" style={{ background: '#10b981' }}></span>
            </div>
            <div className="p-6 min-h-64 flex flex-col items-center justify-center text-center" style={{ color: '#71717a' }}>
              <div className="w-16 h-16 rounded-xl mb-4 flex items-center justify-center text-3xl" style={{ background: '#1a1a25' }}>🤖</div>
              <p>AI Platform Dashboard UI</p>
              <p className="text-xs mt-2">Dashboard Screenshot Placeholder</p>
            </div>
          </div>
          <div>
            <h2 className="text-4xl font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              AI <span style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>SaaS</span> Platform
            </h2>
            <p className="text-lg mb-8" style={{ color: '#a1a1aa', lineHeight: 1.8 }}>Unlock the power of artificial intelligence with our comprehensive suite of tools.</p>
            <div className="grid grid-cols-2 gap-5 mb-8">
              {[{ icon: '⚙️', t: 'Automation Tools', p: 'Streamline workflows' }, { icon: '📄', t: 'Document Processing', p: 'AI-powered extraction' }, { icon: '🧩', t: 'Decision Intelligence', p: 'Data-driven decisions' }, { icon: '💬', t: 'NLP & ML Models', p: 'Advanced language AI' }].map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl border" style={{ background: '#15151f', borderColor: '#27272a' }}>
                  <div className="w-8 h-8 rounded flex items-center justify-center text-lg flex-shrink-0" style={{ background: `${primaryColor}26`, color: primaryColor }}>{f.icon}</div>
                  <div>
                    <h4 className="text-sm font-semibold mb-0.5">{f.t}</h4>
                    <p className="text-xs" style={{ color: '#71717a' }}>{f.p}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 rounded-xl border" style={{ background: '#1a1a25', borderColor: '#27272a' }}>
              <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#71717a' }}>Use Cases</div>
              <div className="flex flex-wrap gap-2">
                {['Enterprise Automation', 'Customer Support', 'Data Analytics', 'Process Optimization'].map(t => (
                  <span key={t} className="px-3 py-1.5 rounded text-sm border" style={{ background: '#15151f', borderColor: '#27272a', color: '#a1a1aa' }}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-24" style={{ background: '#12121a' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16" style={{ maxWidth: '700px', margin: '0 auto 64px' }}>
            <span className="inline-block text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: primaryColor }}>Use Cases</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Transforming Industries</h2>
            <p className="text-lg" style={{ color: '#a1a1aa', lineHeight: 1.7 }}>Discover how {companyName} powers innovation across multiple sectors.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '🏦', title: 'Financial Institutions', desc: 'Modernize debt collection, risk assessment, and customer onboarding with AI-driven automation.' },
              { icon: '🏗️', title: 'Smart Infrastructure', desc: 'Connect and monitor building systems, utilities, and public infrastructure in real-time.' },
              { icon: '🏭', title: 'Manufacturing', desc: 'Enable predictive maintenance, quality control, and supply chain optimization.' },
              { icon: '🚚', title: 'Logistics', desc: 'Track assets, optimize routes, and monitor fleet health with integrated IoT solutions.' },
              { icon: '🏢', title: 'Enterprise Automation', desc: 'Streamline operations, automate document workflows, and enhance decision-making.' },
              { icon: '🛒', title: 'Retail & E-commerce', desc: 'Personalize customer experiences, optimize inventory, and predict demand.' }
            ].map((uc, i) => (
              <div key={i} className="rounded-2xl p-7 border transition-all duration-300 hover:-translate-y-1"
                style={{ background: '#15151f', borderColor: '#27272a' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = primaryColor; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#27272a'; }}>
                <div className="w-12 h-12 rounded-xl mb-5 flex items-center justify-center text-2xl" style={gradientStyle()}>{uc.icon}</div>
                <h3 className="text-xl font-semibold mb-2.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{uc.title}</h3>
                <p className="text-sm" style={{ color: '#a1a1aa', lineHeight: 1.6 }}>{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Nevixa */}
      <section id="why-nevixa" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16" style={{ maxWidth: '700px', margin: '0 auto 64px' }}>
            <span className="inline-block text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: primaryColor }}>Why Nevixa</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Enterprise-Grade Platform</h2>
            <p className="text-lg" style={{ color: '#a1a1aa', lineHeight: 1.7 }}>Built for scale, security, and reliability.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🔒', title: 'Secure Infrastructure', desc: 'Enterprise-grade security with SOC 2 compliance, encryption, and role-based access control.' },
              { icon: '📈', title: 'Scalable Architecture', desc: 'Cloud-native design that scales automatically to handle millions of transactions.' },
              { icon: '🧠', title: 'AI-Powered Insights', desc: 'Advanced analytics and machine learning to drive smarter business decisions.' },
              { icon: '🔗', title: 'Easy Integrations', desc: 'Connect with your existing systems through robust APIs and pre-built connectors.' }
            ].map((w, i) => (
              <div key={i} className="text-center p-8 rounded-2xl border transition-all"
                style={{ background: '#15151f', borderColor: '#27272a' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = primaryColor; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#27272a'; }}>
                <div className="w-16 h-16 rounded-xl mb-5 mx-auto flex items-center justify-center text-3xl" style={{ background: '#1a1a25' }}>{w.icon}</div>
                <h3 className="text-lg font-semibold mb-2.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{w.title}</h3>
                <p className="text-sm" style={{ color: '#a1a1aa', lineHeight: 1.6 }}>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="relative py-28 text-center overflow-hidden" style={{ background: '#0a0a0f' }}>
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 80% 50% at 50% 100%, ${primaryColor}40, transparent)` }}></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Start Building with <span style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{companyName}</span>
          </h2>
          <p className="text-xl mb-10" style={{ color: '#a1a1aa', maxWidth: '600px', margin: '0 auto 40px' }}>
            Join forward-thinking enterprises transforming their operations with AI, IoT, and intelligent automation.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="#" className="px-7 py-3.5 rounded-lg text-base font-semibold text-white inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all"
              style={gradientStyle()}>
              Book a Demo
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </a>
            <a href="#" className="px-7 py-3.5 rounded-lg text-base font-semibold border text-white hover:bg-white/5 transition-all"
              style={{ borderColor: '#3f3f46' }}>Contact Sales</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: '#12121a', borderTop: '1px solid #27272a', padding: '80px 0 40px' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <a href="#" className="flex items-center gap-2 text-2xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <div className="w-9 h-9 rounded flex items-center justify-center text-lg font-extrabold" style={gradientStyle()}>{companyName[0]}</div>
                {companyName}
              </a>
              <p className="text-sm mb-5 max-w-xs" style={{ color: '#a1a1aa', lineHeight: 1.7 }}>
                Next-generation enterprise platform combining AI, IoT, and financial automation for modern businesses.
              </p>
              <div className="flex gap-3">
                {['in', '𝕏', '⌘'].map((s, i) => (
                  <a key={i} href="#" className="w-10 h-10 rounded flex items-center justify-center border text-sm transition-all hover:bg-opacity-90"
                    style={{ background: '#1a1a25', borderColor: '#27272a', color: '#a1a1aa' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = primaryColor; e.currentTarget.style.borderColor = primaryColor; e.currentTarget.style.color = 'white'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#1a1a25'; e.currentTarget.style.borderColor = '#27272a'; e.currentTarget.style.color = '#a1a1aa'; }}>{s}</a>
                ))}
              </div>
            </div>
            {[
              { title: 'Platform', links: ['Debt Recovery', 'IoT Platform', 'AI Solutions', 'Pricing'] },
              { title: 'Company', links: ['About Us', 'Careers', 'Blog', 'Press'] },
              { title: 'Resources', links: ['Documentation', 'API Reference', 'Support', 'Status'] }
            ].map((col, i) => (
              <div key={i}>
                <h4 className="text-sm font-semibold uppercase tracking-wider mb-5" style={{ color: '#ffffff' }}>{col.title}</h4>
                <ul className="space-y-3">
                  {col.links.map(link => (
                    <li key={link}><a href="#" className="text-sm transition-colors hover:underline" style={{ color: '#a1a1aa' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = primaryColor; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#a1a1aa'; }}>{link}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-8" style={{ borderTop: '1px solid #27272a' }}>
            <p className="text-sm" style={{ color: '#71717a' }}>© 2026 {companyName}. All rights reserved.</p>
            <div className="flex gap-6">
              {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map(t => (
                <a key={t} href="#" className="text-sm transition-colors" style={{ color: '#71717a' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#ffffff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#71717a'; }}>{t}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NevixaLandingPage;